import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { CHARITY_HOST, Environment, platformOffPercentage } from './enums';
import { getDamageWaiverFeeAmount } from './damageWaiver';
import CryptoJS from 'crypto-js';
const secretKey = process.env.REACT_APP_ENCRYPTION_KEY;

export const charityOrganizations = [
  { 'friends-of-the-san-juans': 'Friends of the San Juans', code: 'CHARITY_ID_FSJ' },
  { 'northwest-straits-foundation': 'Northwest Straits Foundation', code: 'CHARITY_ID_NSF' },
  { 'puget-soundkeeper-alliance': 'Puget Soundkeeper Alliance', code: 'CHARITY_ID_PSA' },
];

const firstNonBlankValue = (...values) => {
  const foundValue = values.find(value => String(value || '').trim());
  return foundValue == null ? null : String(foundValue).trim();
};

export const getSelectedCharityOrganization = publicData => {
  const charityOptions = publicData?.charityOptions || {};
  const inspectorOptions = publicData?.inspectorOptions || {};

  return firstNonBlankValue(
    charityOptions.charityId,
    publicData?.charityId,
    charityOptions.selectedOrganization,
    charityOptions.inspectorId,
    inspectorOptions.charityId,
    inspectorOptions.selectedOrganization,
    inspectorOptions.inspectorId,
    publicData?.inspectorId
  );
};

/**
 * Returns a capitalized string.
 * @param {string} str - The string to capitalize.
 * @returns {string|null} The capitalized string, or null if input is invalid.
 */
export const capitalizeFirstLetter = str => {
  if (typeof str !== 'string' || str.length === 0) {
    return null;
  }

  return (str.charAt(0).toUpperCase() + str.slice(1)).trim();
};

/**
 * Checks if an array is valid (not null, undefined, or empty).
 * @param {array} arr - The array to check.
 * @returns {boolean} True if the array is valid, false otherwise.
 */
export const isArrayLength = arr => {
  // Check if the input parameter is an array and has a length greater than zero.
  return Array.isArray(arr) && (arr.length > 0 ?? false);
};

export const getProductImages = (images = []) =>
  Array.isArray(images)
    ? images.flatMap(({ attributes: { variants = {} } = {} }) => {
        const url = variants['scaled-medium']?.url ?? variants['listing-card-2x']?.url;
        return url ? [url] : [];
      })
    : [];

export function isFunction(value) {
  return typeof value === 'function';
}

export const useStateRef = initialState => {
  const [state, setState] = useState(initialState);
  const ref = useRef(initialState);

  const dispatch = useCallback(stateToSet => {
    ref.current = isFunction(stateToSet) ? stateToSet(ref.current) : stateToSet;
    setState(ref.current);
  }, []);

  return [state, dispatch, ref];
};

export const useDebouncedValue = (value, delay = 300) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
};

const imageExtensions = [
  'avif',
  'gif',
  'jpg',
  'jpeg',
  'png',
  'tiff',
  'bmp',
  'heic',
  'heif',
  'svg',
  'webp',
];
const videoExtensions = [
  'mpg',
  'mp2',
  'mpeg',
  'mpe',
  'mpv',
  'mp4',
  'mov',
  'wmv',
  'flv',
  'avi',
  'hevc',
];

export const getAttachmentType = att => {
  const attachment = att?.attributes?.content;
  if (typeof attachment !== 'string' || !attachment.startsWith('https://')) {
    return null;
  }

  const extension = attachment
    .split(/[#?]/)[0]
    .split('.')
    .pop()
    .trim();

  const lowerCaseExtension = extension && typeof extension === 'string' && extension.toLowerCase();
  const hasImageExtension = imageExtensions.includes(lowerCaseExtension);
  const hasVideoExtension = videoExtensions.includes(lowerCaseExtension);
  const isSharetribeImageUrl = /^https:\/\/[^/]*sharetribe[^/]*\.imgix\.net\//i.test(attachment);

  if (hasImageExtension || isSharetribeImageUrl) {
    return { type: 'image', link: attachment };
  } else if (hasVideoExtension) {
    return { type: 'video', link: attachment };
  }

  return null;
};

export const formatDateWithRelativity = createdAt => {
  // Use Moment.js to parse the createdAt date
  const date = moment(createdAt);

  // Define custom formatting rules
  const formatRules = {
    sameDay: '[Today], h:mm A', // Display "Today" with time for the current day
    nextDay: '[Tomorrow]', // This can remain as per your original structure or adjusted as needed
    nextWeek: 'dddd', // Day of the week for next week
    lastDay: '[Yesterday]', // Display "Yesterday" for the previous day
    lastWeek: '[Last] dddd', // Display "Last" with the day of the week for the previous week
    sameElse: 'DD/MM/YYYY', // Default date format for dates that don't match above rules
  };

  // Format the createdAt date using the defined rules
  return date.calendar(null, formatRules);
};

export const keepValidGermanChars = inputString => {
  // This regex matches all characters that aren't part of the German alphabet, numerics or spaces
  const regex = /[^a-zA-Z0-9äöüß ]/g;

  // Remove text enclosed within square brackets followed by a URL enclosed within parentheses
  const updatedString = inputString.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');

  // Replace all matches with an empty string
  return updatedString.replace(regex, ' ');
};
export const shuffleArray = arr => {
  const shuffledArray = [...arr];
  for (let i = shuffledArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffledArray[i], shuffledArray[j]] = [shuffledArray[j], shuffledArray[i]];
  }
  return shuffledArray;
};

export const getBlogTags = blogs => {
  const tags = [];
  for (let i = 0; i < blogs?.length; i++) {
    const blog = blogs[i];
    tags.push(blog?.attributes?.tags);
  }
  const filteredTags = Array.from(new Set(tags.flat()));
  return tags ? filteredTags : [];
};

export const getDefaultLocale = () => {
  const defaultLocale =
    typeof window !== 'undefined' && window.localStorage.getItem('selectedLanguage');

  return defaultLocale || 'de';
};

export const sortArrayByLabel = arr => {
  const defaultLocale = getDefaultLocale();

  const collator = new Intl.Collator(defaultLocale, { sensitivity: 'base' });

  return (
    isArrayLength(arr) &&
    arr.sort((a, b) => {
      return collator.compare(a.label, b.label);
    })
  );
};

/**
 * Truncates a string to the specified length and adds an ellipsis if necessary.
 * @param {string} text - The text to be truncated.
 * @param {number} maxLength - The maximum length of the text allowed before truncating.
 * @returns {string} The truncated text with an ellipsis if it was cut off.
 */
export const truncateText = (text, maxLength) => {
  if (text.length <= maxLength) {
    return text;
  }
  return text.substring(0, maxLength) + '...';
};

// Helper export  to check if exactly one of the values is truthy and value1 is a non-empty array
export function isOneTruthyAndValidArray(value1, value2) {
  // Check if value1 is a non-empty array and value2 is falsy
  const isValue1ValidArray = Array.isArray(value1) && value1.length > 0;
  // Check if exactly one of the values is truthy
  return (isValue1ValidArray && !value2) || (!isValue1ValidArray && value2);
}

export const extractSeoCategoryData = data => {
  const title = data?.attributes?.title;
  const description = data?.attributes?.description;
  const content = data?.attributes?.content;
  const heading = data?.attributes?.heading;
  const url = data?.attributes?.url;
  const headingImage = `${process.env.REACT_APP_STRAPI_BASE_URL}${data?.attributes?.headingImage?.data?.attributes?.url}`;

  return {
    title,
    description,
    content,
    heading,
    headingImage,
    url,
  };
};

const convertToDecimalDegrees = (degrees, minutes = 0, seconds = 0) => {
  return degrees + minutes / 60 + seconds / 3600;
};

const convertFromDecimalDegreesToDDM = decimal => {
  const degrees = Math.floor(Math.abs(decimal));
  const minutes = (Math.abs(decimal) - degrees) * 60;
  const direction = decimal >= 0 ? 'N' : 'S'; // Assuming North/South for latitude or East/West for longitude
  return { degrees, minutes, direction };
};

const convertFromDecimalDegreesToDMS = decimal => {
  const degrees = Math.floor(Math.abs(decimal));
  const minutesDecimal = (Math.abs(decimal) - degrees) * 60;
  const minutes = Math.floor(minutesDecimal);
  const seconds = (minutesDecimal - minutes) * 60;
  const direction = decimal >= 0 ? 'N' : 'S'; // Assuming North/South for latitude or East/West for longitude
  return { degrees, minutes, seconds, direction };
};

export const createLatLongFromCoordinates = (values, format, formApi) => {
  let lat, lng;

  // Convert entered values to DD (Decimal Degrees)
  if (format === 'DD') {
    lat = parseFloat(values.latitudeDD);
    lng = parseFloat(values.longitudeDD);
  } else if (format === 'DDM') {
    lat = convertToDecimalDegrees(
      parseInt(values.latitudeDegrees),
      parseFloat(values.latitudeMinutes)
    );
    lng = convertToDecimalDegrees(
      parseInt(values.longitudeDegrees),
      parseFloat(values.longitudeMinutes)
    );
    // Use the original direction to ensure consistency
    lat *= values.latitudeDirection === 'S' ? -1 : 1;
    lng *= values.longitudeDirection === 'W' ? -1 : 1;
  } else if (format === 'DMS') {
    lat = convertToDecimalDegrees(
      parseInt(values.latitudeDegreesDMS),
      parseInt(values.latitudeMinutesDMS),
      parseFloat(values.latitudeSecondsDMS)
    );
    lng = convertToDecimalDegrees(
      parseInt(values.longitudeDegreesDMS),
      parseInt(values.longitudeMinutesDMS),
      parseFloat(values.longitudeSecondsDMS)
    );
    // Use the original direction to ensure consistency
    lat *= values.latitudeDirectionDMS === 'S' ? -1 : 1;
    lng *= values.longitudeDirectionDMS === 'W' ? -1 : 1;
  } else {
    throw new Error('Unsupported coordinate format');
  }

  // Correct the direction in case of discrepancies
  const correctedLatDirection = lat >= 0 ? 'N' : 'S';
  const correctedLngDirection = lng >= 0 ? 'E' : 'W';

  // Ensure directions align with user-selected values
  const finalLatDirection =
    format === 'DDM' || format === 'DMS' ? values.latitudeDirection : correctedLatDirection;
  const finalLngDirection =
    format === 'DDM' || format === 'DMS' ? values.longitudeDirection : correctedLngDirection;

  // Update form values in all formats
  formApi.change('latitudeDD', lat);
  formApi.change('longitudeDD', lng);

  const { degrees: latDegDDM, minutes: latMinDDM } = convertFromDecimalDegreesToDDM(lat);
  const { degrees: lngDegDDM, minutes: lngMinDDM } = convertFromDecimalDegreesToDDM(lng);

  formApi.change('latitudeDegrees', latDegDDM);
  formApi.change('latitudeMinutes', latMinDDM);
  formApi.change('latitudeDirection', finalLatDirection);
  formApi.change('longitudeDegrees', lngDegDDM);
  formApi.change('longitudeMinutes', lngMinDDM);
  formApi.change('longitudeDirection', finalLngDirection);

  const {
    degrees: latDegDMS,
    minutes: latMinDMS,
    seconds: latSecDMS,
  } = convertFromDecimalDegreesToDMS(lat);
  const {
    degrees: lngDegDMS,
    minutes: lngMinDMS,
    seconds: lngSecDMS,
  } = convertFromDecimalDegreesToDMS(lng);

  formApi.change('latitudeDegreesDMS', latDegDMS);
  formApi.change('latitudeMinutesDMS', latMinDMS);
  formApi.change('latitudeSecondsDMS', latSecDMS);
  formApi.change('latitudeDirectionDMS', finalLatDirection);
  formApi.change('longitudeDegreesDMS', lngDegDMS);
  formApi.change('longitudeMinutesDMS', lngMinDMS);
  formApi.change('longitudeSecondsDMS', lngSecDMS);
  formApi.change('longitudeDirectionDMS', finalLngDirection);

  return {
    latitude: lat,
    longitude: lng,
  };
};

export const updateCoordinatesFields = (latitude, longitude, formApi) => {
  // Update Decimal Degrees (DD)
  formApi.change('latitudeDD', latitude);
  formApi.change('longitudeDD', longitude);

  // Calculate Degrees Decimal Minutes (DDM)
  const latDegrees = Math.floor(Math.abs(latitude));
  const latMinutes = (Math.abs(latitude) - latDegrees) * 60;
  const latDirection = latitude >= 0 ? 'N' : 'S';

  const lonDegrees = Math.floor(Math.abs(longitude));
  const lonMinutes = (Math.abs(longitude) - lonDegrees) * 60;
  const lonDirection = longitude >= 0 ? 'E' : 'W';

  formApi.change('latitudeDegrees', latDegrees);
  formApi.change('latitudeMinutes', latMinutes.toFixed(2));
  formApi.change('latitudeDirection', latDirection);
  formApi.change('longitudeDegrees', lonDegrees);
  formApi.change('longitudeMinutes', lonMinutes.toFixed(2));
  formApi.change('longitudeDirection', lonDirection);

  // Calculate Degrees Minutes Seconds (DMS)
  const latSeconds = (latMinutes % 1) * 60;
  const lonSeconds = (lonMinutes % 1) * 60;

  formApi.change('latitudeDegreesDMS', latDegrees);
  formApi.change('latitudeMinutesDMS', Math.floor(latMinutes));
  formApi.change('latitudeSecondsDMS', latSeconds.toFixed(2));
  formApi.change('latitudeDirectionDMS', latDirection);

  formApi.change('longitudeDegreesDMS', lonDegrees);
  formApi.change('longitudeMinutesDMS', Math.floor(lonMinutes));
  formApi.change('longitudeSecondsDMS', lonSeconds.toFixed(2));
  formApi.change('longitudeDirectionDMS', lonDirection);
};

export const getEnvironment = () => {
  return process.env.REACT_APP_ENV;
};

export const getFlexUrl = env => {
  if (env === Environment.DEVELOPMENT) {
    return (
      process.env.REACT_APP_TEST_FLEX_CONSOLE_URL ||
      'https://console.sharetribe.com/o/onbuoy/m/onbuoy-test'
    );
  }
  return (
    process.env.REACT_APP_LIVE_FLEX_CONSOLE_URL ||
    'https://console.sharetribe.com/o/onbuoy/m/onbuoy'
  );
};

export const convertGeolocationToParams = geolocation => {
  const { lat, lng } = geolocation;

  // Helper function to convert decimal to degrees, minutes, seconds, and direction
  const convertToDMS = (decimal, isLatitude) => {
    const direction = decimal >= 0 ? (isLatitude ? 'N' : 'E') : isLatitude ? 'S' : 'W';

    const absDecimal = Math.abs(decimal);
    const degrees = Math.floor(absDecimal);
    const minutesDecimal = (absDecimal - degrees) * 60;
    const minutes = Math.floor(minutesDecimal);
    const seconds = (minutesDecimal - minutes) * 60;

    return {
      decimalDegrees: decimal,
      degrees: degrees,
      degreesDMS: degrees,
      minutes: minutesDecimal, // Decimal representation of minutes
      minutesDMS: minutes, // Rounded-down minutes
      secondsDMS: seconds.toFixed(5), // Rounded seconds to 5 decimal points
      direction: direction,
      directionDMS: direction,
    };
  };

  // Convert latitude and longitude
  const latitudeDMS = convertToDMS(lat, true);
  const longitudeDMS = convertToDMS(lng, false);

  // Combine into locationParams format
  return {
    latitude: latitudeDMS.decimalDegrees,
    latitudeDD: latitudeDMS.decimalDegrees,
    latitudeDegrees: latitudeDMS.degrees,
    latitudeDegreesDMS: latitudeDMS.degreesDMS,
    latitudeDirection: latitudeDMS.direction,
    latitudeDirectionDMS: latitudeDMS.directionDMS,
    latitudeMinutes: latitudeDMS.minutes,
    latitudeMinutesDMS: latitudeDMS.minutesDMS,
    latitudeSecondsDMS: latitudeDMS.secondsDMS,
    longitude: longitudeDMS.decimalDegrees,
    longitudeDD: longitudeDMS.decimalDegrees,
    longitudeDegrees: longitudeDMS.degrees,
    longitudeDegreesDMS: longitudeDMS.degreesDMS,
    longitudeDirection: longitudeDMS.direction,
    longitudeDirectionDMS: longitudeDMS.directionDMS,
    longitudeMinutes: longitudeDMS.minutes,
    longitudeMinutesDMS: longitudeDMS.minutesDMS,
    longitudeSecondsDMS: longitudeDMS.secondsDMS,
    locationType: 'DD',
  };
};

export const showDashboardLink = user => {
  return [
    'darrengaustin@gmail.com',
    'crew@onbuoy.com',
    'darren@onbuoy.com',
    'office@onbuoy.com',
  ].includes(user?.attributes?.email);
};

export const isInstantBooking = l => {
  const instantBooking = l?.attributes?.publicData?.instantBooking;

  if (Array.isArray(instantBooking)) {
    return instantBooking.some(val => val === true || val === 'true');
  }

  return instantBooking === true || instantBooking === 'true';
};

export const extendBoundsByMiles = (bounds, miles) => {
  const EARTH_RADIUS = 3958.8;
  const latDiff = (miles / EARTH_RADIUS) * (180 / Math.PI);
  const lngDiff =
    ((miles / EARTH_RADIUS) * (180 / Math.PI)) / Math.cos((bounds?.ne?.lat * Math.PI) / 180);

  const neLat = bounds?.ne?.lat + latDiff;
  const neLng = bounds?.ne?.lng + lngDiff;
  const swLat = bounds?.sw?.lat - latDiff;
  const swLng = bounds?.sw?.lng - lngDiff;

  const extendedBounds = `${neLat},${neLng},${swLat},${swLng}`;

  return extendedBounds;
};

export function mergeMessageAttributes(transactions) {
  const txs = transactions?.data?.data || [];
  const included = transactions?.data?.included || [];

  const messageMap = {};
  included.forEach(item => {
    if (item.type === 'message') {
      const id = item.id.uuid || item.id;
      messageMap[id] = item.attributes;
    }
  });

  txs.forEach(tx => {
    const messages = tx.relationships?.messages?.data;
    if (Array.isArray(messages)) {
      messages.forEach(msg => {
        const msgId = msg.id.uuid || msg.id;
        if (messageMap[msgId]) {
          msg.attributes = messageMap[msgId];
        }
      });
    }
  });

  return txs;
}

export const isFixedTransaction = transitionId => {
  const fixedIdsEnv = process.env.REACT_APP_CUSTOM_FIXED_TXIDS || '';
  const fixedIds = fixedIdsEnv?.split(',').map(id => id?.trim());
  return fixedIds?.includes(transitionId);
};

export const generateRandomUserData = () => {
  const randomString = Math.random()
    .toString(36)
    .substring(2, 10);
  const firstName = `User${randomString}`;
  const lastName = `Smith${randomString}`;
  const email = `user_${randomString}@yopmail.com`;
  const password = `Pass${randomString}!123`;
  return { firstName, lastName, email, password };
};

export function encryptData(data) {
  if (!data) {
    return null;
  }
  const dataString = typeof data === 'object' ? JSON.stringify(data) : String(data);
  const encryptedData = CryptoJS.AES.encrypt(dataString, secretKey).toString();
  return encryptedData;
}

export function decryptData(encryptedData) {
  if (!encryptedData) {
    return null;
  }
  const bytes = CryptoJS.AES.decrypt(encryptedData, secretKey);
  const decryptedString = bytes.toString(CryptoJS.enc.Utf8);
  try {
    return JSON.parse(decryptedString);
  } catch (e) {
    return decryptedString;
  }
}

// Sorting function that handles multiple criteria
export const sortBlogs = (blogs, sortBy, searchKeyword = '') => {
  if (!blogs || !Array.isArray(blogs)) return [];

  const blogsCopy = [...blogs]; // Avoid mutating original array

  switch (sortBy) {
    case 'recent':
      return blogsCopy.sort(
        (a, b) => new Date(b.attributes.creationDate) - new Date(a.attributes.creationDate)
      );
    case 'oldest':
      return blogsCopy.sort(
        (a, b) => new Date(a.attributes.creationDate) - new Date(b.attributes.creationDate)
      );
    case 'title':
      return blogsCopy.sort((a, b) => a.attributes.title.localeCompare(b.attributes.title));
    case 'relevance':
      if (!searchKeyword) return blogsCopy; // No keyword, return unsorted
      return blogsCopy.sort((a, b) => {
        const keyword = searchKeyword.toLowerCase();
        const getScore = blog => {
          let score = 0;
          const title = blog.attributes.title.toLowerCase();
          const description = blog.attributes.description
            .map(p => p.children.map(c => c.text).join(' '))
            .join(' ')
            .toLowerCase();
          // Score based on keyword matches (exact in title > partial in title > description)
          if (title === keyword) score += 100; // Exact title match
          if (title.includes(keyword)) score += 50; // Partial title match
          if (description.includes(keyword)) score += 20; // Description match
          return score;
        };
        return getScore(b) - getScore(a); // Higher score first
      });
    default:
      return blogsCopy; // Fallback: no sorting
  }
};

export const isThisPropertyCharitable = currentListing => {
  const publicData = currentListing?.author?.attributes?.profile?.publicData;
  const selectedPayout = publicData?.selectedPayout;
  if (selectedPayout !== 'charity') {
    return null;
  }
  const selectedOrganization = getSelectedCharityOrganization(publicData);
  const match = CHARITY_HOST.find(i => i?.selectedOrganization === selectedOrganization);
  return match ?? null;
};

export const resetBoatFields = form => {
  form.change('vessel', '');
  form.change('boatName', '');
  form.change('year', '');
  form.change('make', '');
  form.change('model', '');
  form.change('type', '');
  form.change('registrationState', '');
  form.change('registrationNumber', '');
  form.change('length', '');
  form.change('beam', '');
  form.change('draft', '');
  form.change('hailingPort', '');
  form.change('insurance', '');
};

function safeBuildLocation(locationParams, listing) {
  // Try publicData.locationParams first
  const latPD = locationParams?.latitudeDD;
  const lngPD = locationParams?.longitudeDD;

  // Optional fallback: Sharetribe listing geolocation (if you have it)
  // NOTE: comment this out if you don't use this field.
  const geo = listing?.attributes?.geolocation;
  const latGeo = geo?.lat ?? geo?.latitude;
  const lngGeo = geo?.lng ?? geo?.longitude;

  const latitude = typeof latPD === 'number' ? latPD : typeof latGeo === 'number' ? latGeo : null;
  const longitude = typeof lngPD === 'number' ? lngPD : typeof lngGeo === 'number' ? lngGeo : null;

  if (latitude == null || longitude == null) return null;

  return {
    latitude,
    longitude,
    navigateUrl: `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`,
  };
}

export const generateEmailData = (pageData, currentUser) => {
  const { orderData, listing } = pageData || {};
  const { bookingDates } = orderData || {};
  const author = listing?.author;
  const { displayName, bio } = author?.profile || {};

  // Dates (don’t assume they exist or are valid)
  const start = moment(bookingDates?.bookingStart);
  const end = moment(bookingDates?.bookingEnd);

  const formatTime = m => (m?.isValid() ? m.format('hA') : '');

  const loc = safeBuildLocation(listing?.attributes?.publicData?.locationParams, listing);

  const imageUrl = listing?.images?.[0]?.attributes?.variants?.['listing-card']?.url || '';
  const imageProfileUrl = author?.profileImage?.attributes?.variants?.['listing-card']?.url || '';

  return {
    bookingTime:
      formatTime(start) && formatTime(end) ? `${formatTime(start)} - ${formatTime(end)}` : '',

    listingImage: imageUrl,
    hostName: displayName || '',
    hostDescription: bio || '',
    hostImage: imageProfileUrl,

    // Map bits (present only if we could resolve them)
    latitude: loc?.latitude ?? null,
    longitude: loc?.longitude ?? null,
    navigateUrl: loc?.navigateUrl ?? null,
  };
};

function buildLocationHelper(locationParams) {
  if (!locationParams || !locationParams.latitudeDD || !locationParams.longitudeDD) {
    throw new Error('Invalid locationParams object');
  }

  const latitude = locationParams.latitudeDD;
  const longitude = locationParams.longitudeDD;
  const navigateUrl = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;

  return {
    latitude,
    longitude,
    navigateUrl,
  };
}

export const normalizeLastInspection = lastInspection => {
  if (lastInspection == null || lastInspection === '') {
    return null;
  }

  const value =
    typeof lastInspection === 'object' && !(lastInspection instanceof Date)
      ? lastInspection.date ?? lastInspection.value ?? lastInspection.timestamp
      : lastInspection;

  if (value == null || value === '') {
    return null;
  }

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return null;
    }

    // Inspection dates are calendar dates. Store the selected local calendar day as UTC midnight
    // so that displaying the value in another timezone cannot move it to an adjacent day.
    return Math.floor(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()) / 1000);
  }

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return null;
    }

    return Math.floor(value > 9999999999 ? value / 1000 : value);
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    const numericValue = Number(trimmedValue);

    if (trimmedValue && Number.isFinite(numericValue)) {
      return normalizeLastInspection(numericValue);
    }

    const dateOnlyMatch = trimmedValue.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|T)/);
    if (dateOnlyMatch) {
      const [, yearValue, monthValue, dayValue] = dateOnlyMatch;
      const year = Number(yearValue);
      const month = Number(monthValue) - 1;
      const day = Number(dayValue);
      const timestamp = Date.UTC(year, month, day);
      const parsedDate = new Date(timestamp);

      return parsedDate.getUTCFullYear() === year &&
        parsedDate.getUTCMonth() === month &&
        parsedDate.getUTCDate() === day
        ? Math.floor(timestamp / 1000)
        : null;
    }

    const timestamp = new Date(trimmedValue).getTime();
    return Number.isNaN(timestamp) ? null : Math.floor(timestamp / 1000);
  }

  return null;
};

export const parseLastInspectionDate = lastInspection => {
  const lastInspectionUnix = normalizeLastInspection(lastInspection);

  if (lastInspectionUnix == null || lastInspectionUnix <= 0) {
    return null;
  }

  const date = new Date(lastInspectionUnix * 1000);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const lastInspectionDateToLocalDate = lastInspection => {
  const date = parseLastInspectionDate(lastInspection);

  return date ? new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()) : null;
};

export const LAST_INSPECTION_DATE_FORMAT_OPTIONS = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
};

// Keep the conversion name introduced on test as a compatibility alias.
export const lastInspectionToDate = parseLastInspectionDate;

/**
 * Calculate the final price with waiver fee and platform percentage.
 *
 * @param {number} amount - Base price amount in cents (from price?.amount).
 * @param {object} damageWaiverConfig - Safe public Damage Waiver configuration.
 * @param {number} [platformPercentage=platformOffPercentage] - Platform fee percentage.
 * @returns {string} - Final price formatted to two decimals.
 */
export function calculateFinalPrice(
  amount,
  damageWaiverConfig,
  platformPercentage = platformOffPercentage
) {
  if (typeof amount !== 'number') return '0.00';
  if (amount === 0) return '$0.00';

  const platformFee = (amount * platformPercentage) / 100;
  const total = (amount + getDamageWaiverFeeAmount(damageWaiverConfig) + platformFee) / 100;

  return `$${Number(total).toFixed(2)}`;
}

export const formatDateRange = (startDate, endDate) => {
  const start = moment(startDate).format('MMM Do');
  const end = moment(endDate).format('MMM Do');
  return `${start} to ${end}`;
};

export const getUserPhoneNumber = user => {
  const phoneNumber = user?.attributes?.profile?.protectedData?.phoneNumber;
  return phoneNumber;
};

export const sendTextNotifications = user => {
  const phoneNumber = user?.attributes?.profile?.protectedData?.textNotificationsOptedIn;
  return phoneNumber;
};

export function getFormattedBookingDates(bookingDates) {
  try {
    if (!bookingDates) return null; // Check for bookingDates instead of booking

    const start = moment(bookingDates?.bookingStart).format('dddd, MMM D'); // Format start date
    const end = moment(bookingDates?.bookingEnd).format('dddd, MMM D'); // Format end date

    return { start, end };
  } catch (err) {
    console.error('Error extracting and formatting booking dates:', err);
    return null;
  }
}

export const shouldShowNotificationModal = userProtectedData => {
  if (typeof window === 'undefined') return true;

  // if the key exists (no matter true or false), skip showing the modal
  if (userProtectedData && userProtectedData.hasOwnProperty('textNotificationsOptedIn')) {
    return false;
  }

  const path = window.location.pathname.toLowerCase();
  if (path.includes('/order/') || path.includes('/sale/')) {
    return false;
  }

  return true;
};

export const decryptNumber = (encryptedNumber, key) => {
  if (!encryptedNumber || !key) return null;
  const decrypted = encryptedNumber ^ key;
  return decrypted;
};

export const getCurrentUserPublicMoorageCheckIn = currentUser => {
  const publicMoorageCheckIn = currentUser?.attributes?.profile?.privateData?.publicMoorageCheckIn;

  // Check if the object exists first
  if (!publicMoorageCheckIn || !publicMoorageCheckIn.expiresAt) {
    return false;
  }
  if (publicMoorageCheckIn?.endedAt) {
    return false;
  }
  if (publicMoorageCheckIn?.status && publicMoorageCheckIn?.status !== 'active') {
    return false;
  }

  const expiryDate = new Date(publicMoorageCheckIn.expiresAt);
  const now = new Date();

  // Returns true if expiryDate is greater than the current time
  return expiryDate > now;
};

export const formatCategoryLabel = category => {
  if (!category) return '';

  return (
    category
      // 1. Add a space before any capital letters (for mooringBuoy -> mooring Buoy)
      .replace(/([A-Z])/g, ' $1')
      // 2. Replace underscores with spaces (for public_moorage -> public moorage)
      .replace(/_/g, ' ')
      // 3. Trim extra spaces and capitalize the very first letter
      .trim()
      .replace(/^\w/, c => c.toUpperCase())
      // 4. Lowercase the rest of the words (optional, for "Sentence case")
      .toLowerCase()
      .replace(/^\w/, c => c.toUpperCase())
  );
};
